/** Thin fetch wrapper: same-origin /api, cookie session, typed errors. */
export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody,
  ) {
    super(body.message);
  }
}

const base = import.meta.env.VITE_API_BASE ?? '/api';

/** The body as JSON; an HTML page (nginx while the API restarts, 502/504) becomes a readable error. */
function parse(res: Response, text: string): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    const message = [502, 503, 504].includes(res.status)
      ? 'Serveren svarer ikke lige nu (den genstarter måske efter en opdatering). Prøv igen om et øjeblik.'
      : `Uventet svar fra serveren (HTTP ${res.status})`;
    throw new ApiError(res.status, { code: 'bad_gateway', message });
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', 'X-Requested-With': 'thebb-admin' };
  let body: BodyInit | undefined;
  if (init.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(init.json);
  }
  const res = await fetch(`${base}${path}`, { credentials: 'include', ...init, headers: { ...headers, ...(init.headers as Record<string, string>) }, body });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = parse(res, text);
  if (!res.ok) {
    const err = (data as ApiErrorBody | undefined) ?? { code: 'http_error', message: res.statusText };
    throw new ApiError(res.status, err);
  }
  return data as T;
}

export const get = <T>(path: string) => api<T>(path);
export const post = <T>(path: string, json?: unknown) => api<T>(path, { method: 'POST', json });
export const patch = <T>(path: string, json?: unknown) => api<T>(path, { method: 'PATCH', json });
export const put = <T>(path: string, json?: unknown) => api<T>(path, { method: 'PUT', json });
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });

/** Multipart upload of one file as field `file`. */
export async function upload<T>(path: string, file: File): Promise<T> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${base}${path}`, { method: 'POST', credentials: 'include', headers: { Accept: 'application/json', 'X-Requested-With': 'thebb-admin' }, body: fd });
  const text = await res.text();
  const data = parse(res, text);
  if (!res.ok) throw new ApiError(res.status, (data as ApiErrorBody | undefined) ?? { code: 'http_error', message: res.statusText });
  return data as T;
}
